import {
    Box,
    Chip,
    FormControl,
    FormHelperText,
    Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { isValidEmail } from '../../utils/EntityAttributesUtils'
import TruncatedText from './TruncatedText'
import { CancelRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

interface EmailInputMultiProps {
    id: string
    label: string
    value: string[]
    onChange: (id: string, value: string | string[]) => void
    required?: boolean
    placeholder?: string
    disabled?: boolean
    errorMessage?: string
}

const EmailInputMulti: React.FC<EmailInputMultiProps> = ({
    id,
    label,
    value,
    onChange,
    required = false,
    placeholder,
    disabled = false,
    errorMessage,
}) => {
    const [inputValue, setInputValue] = useState('')
    const [emails, setEmails] = useState<string[]>([])
    const [error, setError] = useState('')
    const { t } = useTranslation()

    const [isFocused, setIsFocused] = useState(false)
    const [, setShowCount] = useState(emails.length)

    useEffect(() => {
        if (!isFocused) {
            // Show only what fits in one line (~ estimate width)
            const maxVisibleChips = 3 // tweak this based on your layout
            setShowCount(Math.min(emails.length, maxVisibleChips))
        } else {
            setShowCount(emails.length)
        }
    }, [isFocused, emails])

    useEffect(() => {
        setEmails(value || [])
    }, [value])

    useEffect(() => {
        setError(errorMessage || '')
    }, [errorMessage])

    const addEmail = (email: string) => {
        const trimmed = email.trim()
        if (trimmed === '') return
        if (!isValidEmail(trimmed)) {
            setError(t('invalidEmailmulti'))
            return
        }
        if (emails.includes(trimmed)) {
            setError(t('duplicateEmail'))
            return
        }
        const updated = [...emails, trimmed]
        setEmails(updated)
        onChange(id, updated)
        setInputValue('')
        setError('')
    }

    const handleDelete = (toDelete: string) => {
        const updated = emails.filter((email) => email !== toDelete)
        setEmails(updated)
        onChange(id, updated)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value)
        if (error) setError('')
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (
            (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') &&
            inputValue
        ) {
            e.preventDefault()
            addEmail(inputValue)
        }
    }

    const handleBlur = () => {
        if (inputValue) {
            addEmail(inputValue)
        }
    }

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth>
                <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
                    {label}
                    {required ? '*' : ''}
                </Typography>
                <Box
                    id={id}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        padding: '4px 8px',
                        border: isFocused ? '2px solid' : '1px solid',
                        borderColor: error
                            ? 'error.main'
                            : isFocused
                              ? 'primary.main'
                              : '#bdbdbd',
                        borderRadius: 1,
                        minHeight: '40px',
                        maxHeight: '120px',
                        overflowY: 'auto',
                        // maxHeight: isFocused ? '120px' : '40px', // 👈 expand on focus
                        // overflowY: isFocused ? 'auto' : 'hidden', // 👈 scroll only when focused
                        cursor: disabled ? 'not-allowed' : 'text',
                        backgroundColor: 'inherit',
                        transition: 'all 0.2s ease',
                        ':hover': {
                            borderColor: !isFocused ? 'ActiveBorder' : '',
                        },
                    }}
                    onClick={() => {
                        if (!disabled) {
                            setIsFocused(true)
                            document.getElementById(`input-${id}`)?.focus()
                        }
                    }}
                    onBlur={(e) => {
                        // prevent blur from triggering on internal chip delete
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                            setIsFocused(false)
                        }
                    }}
                    tabIndex={-1} // to allow blur to work
                >
                    {emails.map((email) => (
                        <Chip
                            id={email}
                            key={email}
                            label={
                                <TruncatedText
                                    text={email}
                                    sx={{ fontSize: '14px' }}
                                ></TruncatedText>
                            }
                            onClick={(e) => {
                                e.stopPropagation()
                                if (!disabled) {
                                    setIsFocused(true)
                                    document
                                        .getElementById(`input-${id}`)
                                        ?.focus()
                                }
                            }}
                            deleteIcon={
                                <CancelRounded id={`delete-icon-${email}`} />
                            }
                            onDelete={() => handleDelete(email)}
                            sx={{ m: '2px', maxWidth: 'calc(100% - 5px)' }}
                            size="small"
                            disabled={disabled}
                        />
                    ))}

                    <input
                        id={`input-${id}`}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onBlur={() => {
                            handleBlur()
                            setIsFocused(false)
                        }}
                        onFocus={() => setIsFocused(true)}
                        placeholder={placeholder || t('enterLabel', { label })}
                        disabled={disabled}
                        style={{
                            minWidth: '100px',
                            flex: '1 1 100px',
                            border: 'none',
                            outline: 'none',
                            fontSize: '14px',
                            opacity: inputValue ? 1 : 0.5,
                            background: 'transparent',
                            padding: '6px 4px',
                        }}
                    />
                </Box>

                <FormHelperText error={!!error}>{error}</FormHelperText>
            </FormControl>
        </Box>
    )
}

export default EmailInputMulti
